import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

import EditProfile from "app/Pages/Settings/sections/EditProfile";
import NotificationPreferences from "./sections/NotificationPreferences";
import HostRegister from "./sections/HostRegister";




interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      sx={{ flex: 1, p: { xs: 2, md: 3 } }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    "aria-controls": `settings-tabpanel-${index}`,
  };
}

const VerticalTabs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const tabs = [
    {
      label: "Profile",
      icon: <PersonIcon />,
      component: <EditProfile />
    },
    {
      label: "Notifications",
      icon: <NotificationsIcon />,
      component: <NotificationPreferences />
    },
    {
      label: "Host Registration",
      icon: <BusinessIcon />,
      component: <HostRegister />
    }
  ];

  if (isMobile) {
    return (
      <Box>
        <Paper 
          elevation={2} 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2
          }}
        >
          <Tabs 
            value={value} 
            onChange={handleChange}
            variant="fullWidth"
            aria-label="Settings tabs"
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.primary',
              },
              '& .Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                {...a11yProps(index)}
                sx={{
                  flexDirection: 'column',
                  gap: 0.5,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem'
                  }
                }}
              />
            ))}
          </Tabs>
        </Paper>
        
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Box>
    );
  }

  return (
    <Card 
      elevation={2} 
      sx={{ 
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        minHeight: 600
      }}
    >
      <Box 
        sx={{ 
          width: 280,
          bgcolor: 'grey.100',
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        <Tabs
          orientation="vertical"
          value={value}
          onChange={handleChange}
          aria-label="Settings tabs"
          sx={{
            '& .MuiTab-root': {
              alignItems: 'flex-start',
              textAlign: 'left',
              minHeight: 64,
              px: 3,
              py: 2,
              fontSize: '0.95rem',
              fontWeight: 600,
              justifyContent: 'flex-start',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              {...a11yProps(index)}
              sx={{
                flexDirection: 'row',
                gap: 2,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem'
                }
              }}
            />
          ))}
        </Tabs>
      </Box>
      
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Card>
  );
};

export default VerticalTabs;
